import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Sparkles, Check, X } from "lucide-react";

import { createClub, uploadClubImage, updateClubImages } from "../../../services/clubsApi";
import {
  slugify,
  clampStr,
  cn,
  normalizeUploadedImage,
  resolveForPreview,
  CREATION_TOPIC_CHIPS,
  CREATION_TOPIC_GROUPS,
  COMMUNITY_TYPE_PRESETS,
  presetById,
} from "./wizardHelpers";

const STEP_COUNT = 5;

const STEP_COPY = [
  { title: "Name your community", ja: "名付け", hint: "A clear name and short description. You can refine everything later." },
  { title: "Choose a topic", ja: "トピック", hint: "Optional. Saved in your club draft only — not sent to the server." },
  { title: "Community type", ja: "タイプ", hint: "How visible the club is and how people become members." },
  { title: "Icon & banner", ja: "アイコン", hint: "Skip for now if you want — upload anytime from the club page." },
  { title: "Review", ja: "確認", hint: "Confirm details, then create." },
];

function JaAccent({ children, className }) {
  return (
    <span lang="ja" className={cn("text-[11px] font-medium text-sky-300/70 tracking-wide", className)}>
      {children}
    </span>
  );
}

function PillToggle({ label, value, onChange, options }) {
  return (
    <div className="w-full">
      {label ? (
        <div className="mb-2 text-[10px] tracking-[0.34em] uppercase text-slate-400">{label}</div>
      ) : null}
      <div className="inline-flex rounded-full border border-white/[0.1] bg-[#0a1018] p-0.5">
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={cn(
                "px-3.5 py-1.5 rounded-full text-[12px] font-semibold tracking-wide transition",
                active
                  ? "bg-sky-500/15 text-sky-100 border border-sky-400/25 shadow-[inset_0_1px_0_rgba(186,230,253,0.08)]"
                  : "text-slate-300 hover:text-slate-100 hover:bg-white/[0.04]"
              )}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ImagePickerRow({ label, hint, file, setFile, previewUrl, setPreviewUrl, disabled }) {
  function pick(e) {
    const f = e.target.files?.[0] || null;
    setFile(f);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(f ? URL.createObjectURL(f) : "");
  }
  function clear() {
    setFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
  }
  return (
    <div className="mt-4">
      <div className="text-[10px] tracking-[0.34em] uppercase text-slate-400">{label}</div>
      <div className="mt-2 rounded-xl border border-white/[0.1] bg-[#0a1018] p-3.5">
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-xl border border-white/12 bg-white/[0.04] overflow-hidden shrink-0">
            {previewUrl ? (
              <img src={previewUrl} alt="" className="h-full w-full object-cover" draggable={false} />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-slate-600 text-xs">—</div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            {hint ? (
              <div className="text-[13px] text-slate-400 leading-relaxed whitespace-pre-line">{hint}</div>
            ) : null}
            <div className="mt-2 flex flex-wrap gap-2">
              <label
                className={cn(
                  "inline-flex cursor-pointer items-center justify-center h-9 px-3 rounded-xl",
                  "border border-sky-400/15 bg-white/[0.04] text-slate-100 text-sm font-semibold hover:bg-sky-500/10 transition",
                  disabled ? "opacity-50 pointer-events-none" : ""
                )}
              >
                Choose
                <input type="file" accept="image/*" className="hidden" onChange={pick} disabled={disabled} />
              </label>
              {file ? (
                <button
                  type="button"
                  onClick={clear}
                  disabled={disabled}
                  className="h-9 px-3 rounded-xl border border-white/14 text-slate-300 text-sm font-semibold hover:text-slate-100 transition"
                >
                  Remove
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function visJoinLabels(visibility, joinPolicy) {
  const vis =
    visibility === "public" ? "Public" : visibility === "unlisted" ? "Unlisted" : "Private";
  const join =
    joinPolicy === "open" ? "Open join" : joinPolicy === "request" ? "Request to join" : "Invite only";
  return { vis, join };
}

/**
 * Global create-club flow: portal overlay, step wizard, success state.
 * Single instance — opened via {@link useCreateClubModal}.
 */
export default function CreateClubModal({ isOpen, onClose }) {
  const nav = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [topicId, setTopicId] = useState(null);
  const [communityPreset, setCommunityPreset] = useState("public");
  const [matureLabel, setMatureLabel] = useState(false);

  const preset = presetById(communityPreset);
  const visibility = preset.visibility;
  const joinPolicy = preset.join_policy;

  const [clubAvatarFile, setClubAvatarFile] = useState(null);
  const [clubBannerFile, setClubBannerFile] = useState(null);
  const [clubAvatarPreview, setClubAvatarPreview] = useState("");
  const [clubBannerPreview, setClubBannerPreview] = useState("");

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [createdSlug, setCreatedSlug] = useState(null);
  const [createdClub, setCreatedClub] = useState(null);
  const successNavDraftRef = useRef(null);

  const [modules] = useState({
    artFeed: true,
    discussions: true,
    channels: true,
    announcements: true,
    resources: true,
    events: false,
  });
  const [curationMode] = useState("staff");
  const [submissionQueue] = useState({
    enabled: false,
    requiresApproval: true,
    dailyLimit: 3,
  });
  const [discoverable] = useState(true);
  const [accent] = useState("emerald");
  const [postPolicy] = useState("members");
  const [rules] = useState(["Be respectful", "Credit artists"]);

  const topicLabel = useMemo(() => {
    const t = CREATION_TOPIC_CHIPS.find((c) => c.id === topicId);
    return t?.label || null;
  }, [topicId]);

  const slugPreview = useMemo(() => slugify(name) || "your-club", [name]);
  const canStep0 = name.trim().length >= 3 && name.trim().length <= 40;
  const isSuccess = step >= STEP_COUNT;

  const contentRating = matureLabel ? "mature" : "all";

  const previewBanner = clubBannerPreview || null;
  const previewAvatar = clubAvatarPreview || null;

  useEffect(() => {
    if (isOpen) return;
    setStep(0);
    setName("");
    setDescription("");
    setTopicId(null);
    setCommunityPreset("public");
    setMatureLabel(false);
    setClubAvatarFile(null);
    setClubBannerFile(null);
    setClubAvatarPreview((p) => {
      if (p) URL.revokeObjectURL(p);
      return "";
    });
    setClubBannerPreview((p) => {
      if (p) URL.revokeObjectURL(p);
      return "";
    });
    setSaving(false);
    setErr("");
    setCreatedSlug(null);
    setCreatedClub(null);
    successNavDraftRef.current = null;
  }, [isOpen]);

  const dismiss = useCallback(() => {
    if (saving) return;
    onClose();
    if (location.pathname === "/clubs/new") {
      nav("/clubs/discover", { replace: true });
    }
  }, [saving, onClose, location.pathname, nav]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, dismiss]);

  const buildApiPayload = useCallback(() => {
    return {
      name: name.trim(),
      description: clampStr(description.trim(), 160) || "",
      visibility,
      join_policy: joinPolicy,
      cover_image_url: null,
      avatar_image_url: null,
    };
  }, [name, description, visibility, joinPolicy]);

  const goNext = () => {
    setErr("");
    if (step === 0 && !canStep0) {
      setErr("Name must be between 3 and 40 characters.");
      return;
    }
    setStep((s) => Math.min(s + 1, STEP_COUNT));
  };

  const goBack = () => {
    setErr("");
    if (step <= 0) {
      dismiss();
      return;
    }
    setStep((s) => Math.max(0, s - 1));
  };

  const handleCreate = async () => {
    if (saving || !canStep0) return;
    setErr("");
    setSaving(true);
    try {
      const payload = buildApiPayload();
      const created = await createClub(payload);
      const slug = created?.club?.slug || created?.slug;
      if (!slug) throw new Error("Club created but missing slug.");

      let bannerStored = null;
      let avatarStored = null;

      if (clubBannerFile) {
        const up = await uploadClubImage({ slug, file: clubBannerFile, imageType: "banner" });
        bannerStored = normalizeUploadedImage(up);
      }
      if (clubAvatarFile) {
        const up = await uploadClubImage({ slug, file: clubAvatarFile, imageType: "avatar" });
        avatarStored = normalizeUploadedImage(up);
      }

      const bannerPreviewResolved = resolveForPreview(bannerStored);
      const avatarPreviewResolved = resolveForPreview(avatarStored);

      const clubDraft = {
        banner_image_url: bannerStored,
        avatar_image_url: avatarStored,
        settings: {
          modules,
          curationMode,
          submissionQueue,
          contentRating,
          discoverable,
          accent,
          rules,
          joinPolicy,
          postPolicy,
          visibility,
          creationTopicId: topicId,
          creationTopicLabel: topicLabel,
        },
      };

      try {
        localStorage.setItem(`clubDraft:${slug}`, JSON.stringify(clubDraft));
      } catch {
        // ignore
      }

      try {
        if (bannerStored || avatarStored) {
          await updateClubImages(slug, {
            banner_image_url: bannerStored,
            avatar_image_url: avatarStored,
          });
        }
      } catch (e3) {
        console.log("[CreateClubModal] updateClubImages skipped/failed:", e3?.message);
      }

      successNavDraftRef.current = {
        clubDraft: {
          ...clubDraft,
          banner_image_url: bannerPreviewResolved || clubDraft.banner_image_url,
          avatar_image_url: avatarPreviewResolved || clubDraft.avatar_image_url,
        },
      };

      setCreatedSlug(slug);
      setCreatedClub(created?.club || null);
      setStep(STEP_COUNT);
      try {
        window.dispatchEvent(new CustomEvent("ah:joined-clubs-changed"));
      } catch {
        /* ignore */
      }
    } catch (e2) {
      setErr(e2?.message || "Failed to create club.");
    } finally {
      setSaving(false);
    }
  };

  const stepMeta = useMemo(() => {
    if (step >= STEP_COUNT) {
      return { title: "You're all set", ja: "完了", hint: "" };
    }
    return STEP_COPY[step] || STEP_COPY[0];
  }, [step]);

  const reviewLines = useMemo(() => {
    const { vis, join } = visJoinLabels(visibility, joinPolicy);
    const presetTitle = COMMUNITY_TYPE_PRESETS.find((p) => p.id === communityPreset)?.title || vis;
    return [
      { k: "Name", v: name.trim() || "—" },
      { k: "URL", v: `/clubs/${slugPreview}` },
      { k: "Description", v: description.trim() ? clampStr(description.trim(), 120) : "—" },
      { k: "Topic", v: topicLabel || "—" },
      { k: "Type", v: `${presetTitle} · ${vis} · ${join}` },
      {
        k: "Media",
        v: clubBannerFile || clubAvatarFile ? "Banner and/or icon selected" : "Default (add later)",
      },
    ];
  }, [
    name,
    description,
    slugPreview,
    topicLabel,
    visibility,
    joinPolicy,
    communityPreset,
    clubBannerFile,
    clubAvatarFile,
  ]);

  if (!isOpen) return null;

  const modal = (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-6">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/75"
        onClick={dismiss}
        disabled={saving}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-club-modal-title"
        className={cn(
          "relative w-full max-w-[520px] max-h-[min(92vh,720px)] rounded-[22px] flex flex-col overflow-hidden",
          "border border-sky-200/15 bg-[#080c14] text-slate-100 antialiased",
          "shadow-[0_24px_64px_rgba(0,0,0,0.75),0_0_0_1px_rgba(15,23,42,0.9)]"
        )}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-400/25 to-transparent opacity-80" />

        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-white/[0.06] shrink-0">
          <div className="min-w-0 pr-2">
            <p className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[10px] tracking-[0.28em] uppercase text-sky-200/85">
              <span>Create a club</span>
              <JaAccent className="normal-case tracking-normal text-sky-300/70">クラブ作成</JaAccent>
            </p>
            <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <h2
                id="create-club-modal-title"
                className="text-lg sm:text-xl font-extrabold text-slate-50 leading-snug tracking-tight"
              >
                {stepMeta.title}
              </h2>
              <JaAccent>{stepMeta.ja}</JaAccent>
            </div>
            {!isSuccess && stepMeta.hint ? (
              <p className="mt-1.5 text-[13px] text-slate-400 leading-relaxed">{stepMeta.hint}</p>
            ) : null}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!isSuccess ? (
              <span className="text-[11px] font-semibold text-slate-500 tabular-nums pt-0.5">
                {step + 1}/{STEP_COUNT}
              </span>
            ) : (
              <div className="h-9 w-9 rounded-xl bg-sky-500/15 border border-sky-400/25 flex items-center justify-center">
                <Check className="w-5 h-5 text-sky-200" strokeWidth={2.5} />
              </div>
            )}
            <button
              type="button"
              onClick={dismiss}
              disabled={saving}
              className="h-9 w-9 rounded-xl border border-white/[0.1] bg-[#0c121c] text-slate-300 hover:text-slate-100 hover:border-sky-400/25 hover:bg-[#111a28] transition flex items-center justify-center disabled:opacity-40"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {!isSuccess ? (
          <div className="px-5 pt-3 pb-0.5 flex gap-1 shrink-0">
            {Array.from({ length: STEP_COUNT }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  "h-0.5 flex-1 rounded-full transition",
                  i <= step ? "bg-sky-400/55" : "bg-white/[0.08]"
                )}
              />
            ))}
          </div>
        ) : null}

        <div className="px-5 py-5 flex-1 min-h-0 overflow-y-auto overscroll-contain scrollbar-hide">
          {!isSuccess && step === 0 && (
            <div className="space-y-5">
              <div>
                <label className="text-[10px] tracking-[0.34em] uppercase text-slate-400">Club name</label>
                <input
                  value={name}
                  onChange={(e) => setName(clampStr(e.target.value, 40))}
                  placeholder="e.g. Neon City Artists"
                  autoFocus
                  className="mt-2 w-full h-11 px-4 rounded-xl border border-white/[0.1] bg-[#0c121c] text-slate-100 placeholder:text-slate-500 font-semibold outline-none focus:border-sky-400/35 focus:ring-1 focus:ring-sky-400/15 transition"
                />
                <p className="mt-1.5 text-xs text-slate-500">
                  URL <span className="text-slate-400 font-medium">/clubs/{slugPreview}</span>
                </p>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.34em] uppercase text-slate-400">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(clampStr(e.target.value, 160))}
                  placeholder="What is this club about?"
                  rows={3}
                  className="mt-2 w-full px-4 py-3 rounded-xl border border-white/[0.1] bg-[#0c121c] text-slate-100 placeholder:text-slate-500 outline-none focus:border-sky-400/35 focus:ring-1 focus:ring-sky-400/15 transition resize-none"
                />
                <p className="mt-1 text-xs text-slate-500 tabular-nums">{description.length}/160</p>
              </div>
            </div>
          )}

          {!isSuccess && step === 1 && (
            <div className="space-y-1">
              <div className="max-h-[min(48vh,300px)] sm:max-h-[min(50vh,340px)] overflow-y-auto overscroll-contain scrollbar-hide pr-0.5 space-y-4">
                {CREATION_TOPIC_GROUPS.map((group) => (
                  <section key={group.key} aria-labelledby={`create-club-topic-${group.key}`}>
                    <div id={`create-club-topic-${group.key}`} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 mb-2">
                      <h3 className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                        {group.labelEn}
                      </h3>
                      <JaAccent className="text-[10px]">{group.labelJa}</JaAccent>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {group.items.map((c) => {
                        const on = topicId === c.id;
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => setTopicId(on ? null : c.id)}
                            className={cn(
                              "touch-manipulation w-full min-h-[38px] px-2.5 py-1.5 rounded-lg border text-left text-[11px] sm:text-[11.5px] font-medium leading-snug transition",
                              on
                                ? "border-sky-400/45 bg-sky-500/[0.14] text-sky-100 shadow-[inset_0_0_0_1px_rgba(56,189,248,0.1)]"
                                : "border-white/[0.1] bg-[#0c121c] text-slate-300 hover:border-sky-400/22 hover:bg-[#111a28] hover:text-slate-100"
                            )}
                          >
                            {c.label}
                          </button>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            </div>
          )}

          {!isSuccess && step === 2 && (
            <div className="space-y-4">
              <div className="grid gap-2">
                {COMMUNITY_TYPE_PRESETS.map((p) => {
                  const on = communityPreset === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setCommunityPreset(p.id)}
                      className={cn(
                        "text-left rounded-xl border px-4 py-3 transition text-slate-100",
                        on
                          ? "border-sky-400/35 bg-sky-500/[0.1] shadow-[inset_0_1px_0_rgba(186,230,253,0.06)]"
                          : "border-white/[0.1] bg-[#0c121c] hover:border-sky-400/15 hover:bg-[#111a28]"
                      )}
                    >
                      <div className="text-sm font-bold text-slate-50">{p.title}</div>
                      <div className="mt-0.5 text-xs text-slate-400 leading-relaxed">{p.subtitle}</div>
                    </button>
                  );
                })}
              </div>
              <div className="rounded-xl border border-white/[0.1] bg-[#0a1018] px-4 py-3">
                <PillToggle
                  label="Audience label (draft only)"
                  value={matureLabel ? "mature" : "all"}
                  onChange={(v) => setMatureLabel(v === "mature")}
                  options={[
                    { value: "all", label: "All ages" },
                    { value: "mature", label: "18+" },
                  ]}
                />
                <p className="mt-2 text-[11px] text-slate-500 leading-relaxed">
                  Not stored on the club row yet — kept in local draft for UI preferences.
                </p>
              </div>
            </div>
          )}

          {!isSuccess && step === 3 && (
            <div>
              <p className="text-[13px] text-slate-400 mb-3 leading-relaxed">
                Optional — add these now or upload later from your club page.
              </p>
              <ImagePickerRow
                label="Banner image"
                hint="Wide background image for your club."
                file={clubBannerFile}
                setFile={setClubBannerFile}
                previewUrl={clubBannerPreview}
                setPreviewUrl={setClubBannerPreview}
                disabled={saving}
              />
              <ImagePickerRow
                label="Community icon"
                hint="Square profile image for your club."
                file={clubAvatarFile}
                setFile={setClubAvatarFile}
                previewUrl={clubAvatarPreview}
                setPreviewUrl={setClubAvatarPreview}
                disabled={saving}
              />
            </div>
          )}

          {!isSuccess && step === 4 && (
            <div className="space-y-4">
              <dl className="rounded-xl border border-white/[0.1] bg-[#0a1018] divide-y divide-white/[0.06]">
                {reviewLines.map((row) => (
                  <div key={row.k} className="px-4 py-3 flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4">
                    <dt className="text-[10px] uppercase tracking-wider text-slate-500 shrink-0 sm:w-28">{row.k}</dt>
                    <dd className="text-sm text-slate-300 leading-relaxed break-words min-w-0">{row.v}</dd>
                  </div>
                ))}
              </dl>
              <p className="text-xs text-slate-500 text-center leading-relaxed">
                You become the owner. Fine-tune modules, rules, and permissions after creation.
              </p>
            </div>
          )}

          {isSuccess && createdSlug && (
            <div className="space-y-5">
              <div className="text-center">
                <div className="inline-flex items-center gap-2 text-sky-200/90 text-sm font-bold">
                  <Sparkles className="w-4 h-4 text-sky-300/90" />
                  Club created
                </div>
                <p className="mt-2 text-sm text-slate-400">
                  <span className="text-slate-100 font-semibold">{createdClub?.name || name}</span>
                  <span className="text-slate-600"> · </span>
                  <span className="text-slate-400">/clubs/{createdSlug}</span>
                </p>
                {topicLabel ? (
                  <p className="mt-1 text-xs text-slate-500">Topic: {topicLabel}</p>
                ) : null}
              </div>

              <div className="rounded-xl border border-white/[0.1] bg-[#0a1018] px-4 py-3 text-left">
                <p className="text-[10px] tracking-[0.28em] uppercase text-slate-500 mb-2 flex flex-wrap items-baseline gap-2">
                  <span>Next steps</span>
                  <JaAccent className="text-[10px]">次のステップ</JaAccent>
                </p>
                <ul className="text-sm text-slate-400 space-y-2 list-disc list-inside leading-relaxed">
                  <li>Add moderators and club rules</li>
                  <li>Post a welcome thread</li>
                  <li>Customize tabs and feed from the club home</li>
                </ul>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <button
                  type="button"
                  onClick={() => {
                    nav("/community");
                    onClose();
                  }}
                  className="h-11 rounded-xl border border-white/[0.1] bg-white/[0.04] text-sm font-bold text-slate-200 hover:bg-white/[0.07] hover:border-sky-400/15 transition"
                >
                  Go to Community Page
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const payload = successNavDraftRef.current || {
                      clubDraft: {
                        banner_image_url: previewBanner,
                        avatar_image_url: previewAvatar,
                        settings: {
                          modules,
                          curationMode,
                          submissionQueue,
                          contentRating,
                          discoverable,
                          accent,
                          rules,
                          joinPolicy,
                          postPolicy,
                          visibility: createdClub?.visibility || visibility,
                          creationTopicId: topicId,
                          creationTopicLabel: topicLabel,
                        },
                      },
                    };
                    nav(`/clubs/${encodeURIComponent(createdSlug)}`, { state: payload });
                    onClose();
                  }}
                  className="h-11 rounded-xl border border-sky-400/30 bg-sky-500/[0.14] text-sm font-bold text-slate-100 hover:bg-sky-500/[0.2] shadow-[inset_0_1px_0_rgba(186,230,253,0.1)] transition"
                >
                  View Next Steps
                </button>
              </div>
              <p className="text-center text-xs text-slate-500">View Next Steps opens your new club home.</p>
            </div>
          )}

          {err ? (
            <div className="mt-4 rounded-xl border border-red-400/25 bg-red-950/40 px-4 py-3 text-sm text-red-200/95">
              {err}
            </div>
          ) : null}
        </div>

        {!isSuccess ? (
          <div className="px-5 py-4 border-t border-white/[0.08] flex items-center justify-between gap-3 bg-[#060910] shrink-0">
            <button
              type="button"
              onClick={goBack}
              disabled={saving}
              className="inline-flex items-center gap-1 h-10 px-4 rounded-full border border-white/[0.1] bg-white/[0.03] text-sm font-semibold text-slate-300 hover:bg-white/[0.06] hover:border-white/15 hover:text-slate-100 transition disabled:opacity-45"
            >
              <ChevronLeft className="w-4 h-4 opacity-80" />
              {step === 0 ? "Cancel" : "Back"}
            </button>
            {step < STEP_COUNT - 1 ? (
              <button
                type="button"
                onClick={goNext}
                disabled={saving || (step === 0 && !canStep0)}
                className={cn(
                  "inline-flex items-center gap-1 h-10 px-5 rounded-full text-sm font-bold transition",
                  step === 0 && !canStep0
                    ? "border border-white/[0.08] bg-white/[0.02] text-slate-600 cursor-not-allowed"
                    : "border border-sky-400/28 bg-sky-500/[0.14] text-slate-100 hover:bg-sky-500/[0.2] shadow-[inset_0_1px_0_rgba(186,230,253,0.08)]"
                )}
              >
                Next
                <ChevronRight className="w-4 h-4 opacity-90" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleCreate}
                disabled={saving || !canStep0}
                className={cn(
                  "h-10 px-6 rounded-full text-sm font-bold transition",
                  canStep0 && !saving
                    ? "border border-sky-400/30 bg-sky-500/[0.16] text-slate-100 hover:bg-sky-500/[0.22] shadow-[inset_0_1px_0_rgba(186,230,253,0.1)]"
                    : "border border-white/[0.08] bg-white/[0.02] text-slate-600 cursor-not-allowed"
                )}
              >
                {saving ? "Creating…" : "Create club"}
              </button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
